import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDrag } from 'react-dnd';
import { PlanoContext } from './PlanoContext';
import Swal from 'sweetalert2';
import DimensionChangeModal from './DimensionChangeModal';
import { Card, CardContent, Typography, Box, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import './Plano.css';

function ProductCard({ prod, url, TCN, i, setProds, newProds, newURL }) {
  const theme = useTheme();
  const [didDrop, setDidDrop] = useState(false);
  const [show, setShow] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imageRef = useRef(null);
  const { prods, error, setError } = useContext(PlanoContext);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'productList',
      item: { prod: prod },
      end: (item, monitor) => {
        if (!monitor.didDrop()) {
          setDidDrop(true);
        } else {
          setDidDrop(false);
        }
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [prod]
  );

  useEffect(() => {
    if (error != '' && didDrop) {
      Swal.fire(`Can't place the product here`, error, 'error');
      setError('');
      setDidDrop(false);
    }
  }, [didDrop]);

  const finalWidth = () => {
    if (newURL === 'ZTplano') {
      return prod.dimension.length / 3;
    } else {
      return prod.dimension.length;
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Delay image loading by 2 seconds
            const timer = setTimeout(() => {
              setIsImageLoaded(true);
              observer.unobserve(entry.target);
            }, 2000);

            return () => clearTimeout(timer);
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 0.1, // trigger when 10% of the image is visible
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);

  const renderImage = (imageUrl) => {
    return (
      <img
        ref={(el) => {
          drag(el);
          imageRef.current = el;
        }}
        loading="lazy"
        style={{
          display: 'block',
          border: isDragging ? `3px solid ${theme.palette.secondary.main}` : '0px',
          borderRadius: isDragging ? '6px' : '0',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          opacity: isImageLoaded ? 1 : 0.5,
          filter: isImageLoaded ? 'none' : 'blur(5px)',
          transition: 'all 0.3s ease',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
        src={isImageLoaded ? imageUrl : ''}
        alt={prod.name || 'Product'}
      />
    );
  };

  if (prod.empty) {
    return (
      <Card
        elevation={0}
        className="product-card-container"
        sx={{
          border: `1px solid ${alpha(theme.palette.grey[400], 0.25)}`,
          borderRadius: 1.5,
          '&:hover': {
            boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.15)}`,
            borderColor: alpha(theme.palette.grey[500], 0.4),
          },
        }}
      >
        <Box
          className="product-image-box"
          sx={{
            backgroundColor: alpha(theme.palette.grey[100], 0.3),
          }}
        >
          {renderImage(prod.productPictures[0])}
        </Box>
        <CardContent className="product-content-box" sx={{ '&:last-child': { pb: '10px !important' } }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
            Empty Slot
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            Width: {prod.dimension.length} cm
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      className="product-card-container"
      sx={{
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: 1.5,
        backgroundColor: isDragging ? `${alpha(theme.palette.secondary.main, 0.08)} !important` : undefined,
        '&:hover': {
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      }}
    >
      <Box
        className="product-image-box"
        sx={{
          backgroundColor: alpha(theme.palette.grey[50], 0.3),
        }}
      >
        {renderImage(url)}
      </Box>
      <CardContent className="product-content-box" sx={{ '&:last-child': { pb: '10px !important' } }}>
        <Typography variant="subtitle2" fontWeight={600} color="text.primary" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
          {prod.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
          ${prod.price}
        </Typography>
        {!TCN ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, fontSize: '0.8rem' }}>
              Lanes required: {prod.dimension.lane} lanes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, fontSize: '0.8rem' }}>
              Depth required: {prod.dimension.step} steps
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              Vertical slot required: {prod.dimension.height} slots
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, fontSize: '0.8rem' }}>
              Width: {finalWidth()} cm
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.3, fontSize: '0.8rem' }}>
              Depth: {prod.dimension.width} cm
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              Vertical slot required: {prod.dimension.height}
            </Typography>
          </Box>
        )}
      </CardContent>
      <DimensionChangeModal
        newProds={newProds}
        setProds={setProds}
        prod={prod}
        show={show}
        onHide={() => setShow(false)}
        prods={prods}
        i={i}
      />
    </Card>
  );
}

export default ProductCard;
